/**
 * WHERE the governed site-visit series lives in a reporting payload — one
 * answer for every surface that draws either verified campaign-attributed
 * visits or the legacy advertiser-level GA4 overlay.
 *
 * The overlay itself has never been the fragile part. The renderer has drawn it
 * since the cross-platform surfaces adopted the canonical Delivery chart: right
 * axis, gapped path, legend entry, tooltip row. What has repeatedly broken is
 * the far duller question of which property of which object the series was
 * handed to a given surface under — because the reporting spine serves the SAME
 * series through three different envelopes:
 *
 *   1. `{ siteVisits }` on an UNWRAPPED canonical summary — what the welcome
 *      payload hands its card (`welcomeSummary.canonicalSummary`).
 *   2. `{ siteVisits }` at the ROOT of the public report payload — the
 *      projection publishes it beside `canonicalReporting`, never inside it.
 *   3. `{ canonicalReporting: { siteVisits }, canonicalSummary: { siteVisits } }`
 *      — the authenticated `/api/dashboard/performance-summary` ENVELOPE, which
 *      attaches the series to the canonical summary and returns that summary
 *      under both names.
 *
 * Platform Analytics reads envelope 3 and was the only adapter that looked for
 * the series at the root: every other read in its model unwraps
 * `canonicalReporting || canonicalSummary || summary` first, and this one did
 * not. So the page fetched a payload that carried the GA4 days, found nothing
 * at `summary.siteVisits`, and drew delivery with no overlay — silently, since
 * "no observation" and "looked in the wrong place" produce the identical chart.
 * The public report kept working the whole time, on envelope 2, which is why
 * the adapter's own tests passed.
 *
 * A missing overlay is not a cosmetic defect. It is a chart that answers "did
 * the site respond?" with "no" when the honest answer is "yes, and here it is".
 *
 * So the location is resolved here and nowhere else. Every consumer passes
 * whatever it was handed — envelope, summary or bare series — and gets back one
 * normalized series, or null.
 *
 * Byte-identical in the Cortex client and the Quantum superadmin: two portals
 * that disagree about where a series lives draw two different charts from one
 * payload, which is precisely the failure this module ends.
 */

import {
  AD_ATTRIBUTED_SITE_VISITS_CLASSIFICATION,
  AD_ATTRIBUTED_SITE_VISITS_LABEL,
} from "./outcomeSemantics";

/*
 * The advertiser-wide overlay's name. GA4 is where that series comes FROM, and
 * naming it after its source is honest for a measurement of ALL site traffic —
 * it is not a governed outcome and has no other name to be called by.
 */
export const OBSERVED_GA4_SITE_VISITS_LABEL = "Site Visits · GA4";

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function toIsoDay(value) {
  const dateKey = String(value == null ? "" : value).slice(0, 10);
  return ISO_DAY.test(dateKey) ? dateKey : null;
}

function trimmedOrNull(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/*
 * A series shape, not a payload shape: `days` is the one property every
 * envelope agrees on, so it is what identifies the object as the series itself
 * rather than something carrying it.
 */
function looksLikeSeries(candidate) {
  return Boolean(
    candidate && typeof candidate === "object" && Array.isArray(candidate.days)
  );
}

/**
 * The governed site-visit series carried by `source`, normalized — or null.
 *
 * `null` is the truthful answer for "this payload carries no observation", and
 * every caller must keep it distinguishable from an observation of zero. An
 * absent day leaves a HOLE in the overlay; a reported 0 is a plotted zero. The
 * two look nothing alike and mean nothing alike.
 *
 * The place-list is ordered so that no envelope can shadow another: the public
 * payload has a root `siteVisits` and no canonical copy, the authenticated
 * envelope has the canonical copy and no root one, and an unwrapped summary is
 * simply the first case. A series is returned only when it carries at least one
 * usable day — an empty `days` array is an absent series, not an empty one.
 */
export function resolveObservedSiteVisits(source) {
  if (!source || typeof source !== "object") return null;

  const candidate =
    (looksLikeSeries(source.siteVisits) && source.siteVisits) ||
    (looksLikeSeries(source?.canonicalReporting?.siteVisits) &&
      source.canonicalReporting.siteVisits) ||
    (looksLikeSeries(source?.canonicalSummary?.siteVisits) &&
      source.canonicalSummary.siteVisits) ||
    /* Already the series — the per-platform topline paths hand it over bare. */
    (looksLikeSeries(source) && source) ||
    null;
  if (!candidate) return null;

  /*
   * Dates are canonical ISO days or they are dropped. A day the chart cannot
   * key by date cannot be joined to a delivery column, and a series that
   * silently keeps it would report an observation count larger than the number
   * of observations anyone can see.
   */
  const days = candidate.days
    .map((day) => {
      if (!day || typeof day !== "object") return null;
      const date = toIsoDay(day.date);
      if (!date) return null;
      /*
       * `Number(null)` is 0 and `Number("")` is 0. Coercing first would turn
       * "GA4 reported nothing for this day" into "GA4 reported no visitors" —
       * the one substitution this overlay exists to never make. A value is an
       * observation only if something was actually there to read.
       */
      if (day.value == null || day.value === "") return null;
      const value = Number(day.value);
      return Number.isFinite(value) ? { date, value } : null;
    })
    .filter(Boolean);
  if (!days.length) return null;

  const metric = trimmedOrNull(candidate.metric) || "sessions";
  const attributed = metric === AD_ATTRIBUTED_SITE_VISITS_CLASSIFICATION;
  return {
    metric,
    /*
     * The GOVERNED metric names itself, and a label riding the payload cannot
     * outrank it. Vocabulary is the one thing a cached, replayed or
     * hand-assembled envelope must not be able to set: a summary still holding
     * the pre-relabel `Site Visits · GA4` beside the earned metric would put
     * the legacy name back on a verified outcome, on whichever surface read
     * that copy first — which is the same one-payload-two-names defect this
     * module was written to end, arriving through the cache instead of through
     * a second adapter.
     *
     * The generic overlay keeps honouring a stated label: it is not governed
     * vocabulary, and a payload that names its own sessions series is entitled
     * to be believed about it.
     */
    label: attributed
      ? AD_ATTRIBUTED_SITE_VISITS_LABEL
      : trimmedOrNull(candidate.label) || OBSERVED_GA4_SITE_VISITS_LABEL,
    days,
    observedDays: days.length,
    startDate: toIsoDay(candidate.startDate) || days[0].date,
    endDate: toIsoDay(candidate.endDate) || days[days.length - 1].date,
    timeZone: trimmedOrNull(candidate.timeZone) || "",
  };
}

/**
 * The same series as a date → value index, for adapters that join it onto
 * delivery columns.
 *
 * Keyed by the observation's OWN canonical date string, never by position: two
 * sources with different gaps zipped by index move an observation onto a day
 * nobody reported it on. `byDate` is always a Map, so callers can join with
 * `has`/`get` without re-deciding what an absent day means.
 */
export function indexObservedSiteVisitsByDate(source) {
  const series = resolveObservedSiteVisits(source);
  return {
    series,
    byDate: new Map((series?.days || []).map((day) => [day.date, day.value])),
    timeZone: series?.timeZone || "",
    metric: series?.metric || null,
    label: series?.label || null,
  };
}
